import React, { useEffect, useMemo, useState } from 'react';
import {
  FiBell,
  FiChevronDown,
  FiChevronLeft,
  FiChevronUp,
  FiCheckCircle,
  FiCode,
  FiCreditCard,
  FiEdit3,
  FiFilter,
  FiGlobe,
  FiImage,
  FiLink,
  FiLoader,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiSend,
  FiServer,
  FiSettings,
  FiShield,
  FiShoppingBag,
  FiTool,
  FiUploadCloud,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import { api } from '../services/api';
import AdminFieldTranslation from './AdminFieldTranslation';
import { resolveSettingMediaUrl, useSiteSettings } from '../context/SiteSettingsContext';
import {
  filterNotificationEvents,
  getAdminRecipientSummary,
  getNotificationSummary,
  NOTIFICATION_CATEGORIES,
} from '../utils/notificationSettings';
import './AdminSettingsPanel.css';

const SETTINGS_SECTIONS = [
  { id: 'general', label: 'عمومی و برند', description: 'نام، لوگو، رنگ‌ها و فوتر', icon: FiGlobe },
  { id: 'contact', label: 'ارتباط با ما', description: 'راه‌های تماس و شبکه‌های اجتماعی', icon: FiPhone },
  { id: 'email', label: 'ایمیل', description: 'اتصال SMTP و فرستنده', icon: FiMail },
  { id: 'sms', label: 'پیامک', description: 'پنل پیامکی و شماره ارسال', icon: FiMessageSquare },
  { id: 'payment', label: 'پرداخت', description: 'درگاه سپهر و حداقل مبلغ', icon: FiCreditCard },
  { id: 'store', label: 'فروشگاه و دانلود', description: 'فایل، دانلود و شماره سفارش', icon: FiShoppingBag },
  { id: 'notifications', label: 'اعلان‌ها', description: 'رویدادها و قالب پیام‌ها', icon: FiBell },
  { id: 'seo', label: 'سئو و آمار', description: 'متادیتا و ابزارهای تحلیل', icon: FiSearch },
  { id: 'status', label: 'وضعیت سایت', description: 'تعمیرات و ثبت‌نام کاربران', icon: FiTool },
];

const TEMPLATE_VARIABLES = ['{{name}}', '{{user_email}}', '{{user_phone}}', '{{site_name}}', '{{order_number}}', '{{product_name}}', '{{service_name}}', '{{amount}}', '{{remaining_amount}}', '{{status}}', '{{response_hours}}', '{{review_rating}}', '{{review_excerpt}}', '{{review_link}}', '{{contact_ticket}}', '{{contact_topic}}', '{{contact_method}}', '{{contact_message}}', '{{contact_excerpt}}', '{{contact_admin_link}}', '{{panel_link}}', '{{download_link}}', '{{support_email}}'];
const SMS_TEMPLATE_VARIABLES = ['%name%', '%order_number%', '%product_name%', '%service_name%', '%amount%', '%remaining_amount%', '%status%', '%response_hours%', '%review_rating%', '%contact_ticket%', '%contact_topic%'];
const EMAIL_LAYOUT_VARIABLES = ['{{content}}', '{{subject}}', '{{site_name}}', '{{tagline}}', '{{logo_url}}', '{{support_email}}', '{{site_url}}', '{{year}}'];
const NOTIFICATION_CATEGORY_ICONS = {
  account: FiUser,
  communications: FiMessageSquare,
  store: FiShoppingBag,
  services: FiTool,
};

const cloneAndSet = (source, path, value) => {
  const next = JSON.parse(JSON.stringify(source));
  const keys = path.split('.');
  let cursor = next;
  keys.slice(0, -1).forEach((key) => {
    if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[keys[keys.length - 1]] = value;
  return next;
};

const Field = ({ label, help, full = false, children, translationPath, sourceValue, multiline = false }) => (
  <div className={`settings-field ${full ? 'is-full' : ''}`}>
    <label>
      {label}
      {translationPath && (
        <AdminFieldTranslation
          entityType="setting"
          entityId="site"
          fieldPath={translationPath}
          fieldLabel={label}
          sourceValue={sourceValue}
          multiline={multiline}
        />
      )}
    </label>
    {children}
    {help && <small>{help}</small>}
  </div>
);

const Toggle = ({ checked, onChange, label, help }) => (
  <label className="settings-toggle-row">
    <span className={`settings-toggle ${checked ? 'is-on' : ''}`}>
      <input type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
      <i />
    </span>
    <span><strong>{label}</strong>{help && <small>{help}</small>}</span>
  </label>
);

const SectionHeading = ({ icon: Icon, title, description }) => (
  <header className="settings-section-heading">
    <span><Icon /></span>
    <div><h2>{title}</h2><p>{description}</p></div>
  </header>
);

const AdminSettingsPanel = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [testing, setTesting] = useState('');
  const [uploading, setUploading] = useState('');
  const [notice, setNotice] = useState(null);
  const [notificationQuery, setNotificationQuery] = useState('');
  const [notificationCategory, setNotificationCategory] = useState('all');
  const [notificationOnlyActive, setNotificationOnlyActive] = useState(false);
  const [selectedNotificationEvent, setSelectedNotificationEvent] = useState('registration');
  const [notificationAudience, setNotificationAudience] = useState('user');
  const [notificationGuideOpen, setNotificationGuideOpen] = useState(false);
  const [adminRecipientSummary, setAdminRecipientSummary] = useState(null);
  const { refreshSettings } = useSiteSettings();

  const currentSection = useMemo(
    () => SETTINGS_SECTIONS.find((section) => section.id === activeSection) || SETTINGS_SECTIONS[0],
    [activeSection]
  );
  const filteredNotificationEvents = useMemo(
    () => filterNotificationEvents(settings?.notifications?.events, {
      query: notificationQuery,
      category: notificationCategory,
      onlyActive: notificationOnlyActive,
    }),
    [notificationCategory, notificationOnlyActive, notificationQuery, settings]
  );
  const notificationSummary = useMemo(
    () => getNotificationSummary(settings?.notifications?.events),
    [settings]
  );

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([
      api.get('/api/settings'),
      api.get('/api/users'),
    ])
      .then(([settingsResult, usersResult]) => {
        if (!mounted) return;
        if (settingsResult.status !== 'fulfilled' || !settingsResult.value.success) {
          throw settingsResult.reason || new Error('دریافت تنظیمات انجام نشد');
        }

        const nextSettings = settingsResult.value.data;
        if (usersResult.status === 'fulfilled' && usersResult.value.success) {
          setAdminRecipientSummary(getAdminRecipientSummary(
            usersResult.value.data || []
          ));
        } else {
          setAdminRecipientSummary(nextSettings.notifications.adminRecipientSummary || null);
        }
        setSettings(nextSettings);
      })
      .catch((error) => {
        if (mounted) setNotice({ type: 'error', text: error.data?.message || error.message || 'دریافت تنظیمات انجام نشد' });
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const update = (path, value) => setSettings((current) => cloneAndSet(current, path, value));

  const saveSection = async (section) => {
    setSaving(section);
    setNotice(null);
    try {
      const response = await api.put(`/api/settings/${section}`, settings[section]);
      if (response.success) {
        setSettings(response.data);
        await refreshSettings();
        setNotice({ type: 'success', text: response.message || 'تنظیمات ذخیره شد' });
      }
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'ذخیره تنظیمات انجام نشد' });
    } finally {
      setSaving('');
    }
  };

  const uploadImage = async (event, path) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'].includes(file.type)) {
      setNotice({ type: 'error', text: 'فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setNotice({ type: 'error', text: 'حجم تصویر نباید بیشتر از ۵ مگابایت باشد' });
      return;
    }
    setUploading(path);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.upload('/api/settings/media/upload-image', formData);
      if (response.success) update(path, response.data.imageUrl);
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'آپلود تصویر انجام نشد' });
    } finally {
      setUploading('');
    }
  };

  const runTest = async (type, body) => {
    setTesting(type);
    setNotice(null);
    try {
      const response = await api.post(`/api/settings/test/${type}`, body || {});
      setNotice({ type: 'success', text: response.message });
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || 'آزمایش اتصال ناموفق بود' });
    } finally {
      setTesting('');
    }
  };

  if (loading) {
    return <div className="settings-loading"><FiLoader /><span>در حال دریافت تنظیمات...</span></div>;
  }
  if (!settings) {
    return <div className="settings-load-error"><FiServer /><h3>تنظیمات دریافت نشد</h3><button type="button" onClick={() => window.location.reload()}><FiRefreshCw /> تلاش دوباره</button></div>;
  }

  const renderGeneral = () => (
    <>
      <SectionHeading icon={FiGlobe} title="عمومی و برند" description="هویت بصری و اطلاعات پایه‌ای که در سراسر وب‌سایت نمایش داده می‌شوند." />
      <div className="settings-grid">
        <Field label="نام وب‌سایت" help="در منو، فوتر و عنوان صفحات استفاده می‌شود." translationPath="general.siteTitle" sourceValue={settings.general.siteTitle}><input value={settings.general.siteTitle} onChange={(e) => update('general.siteTitle', e.target.value)} /></Field>
        <Field label="شعار کوتاه" translationPath="general.tagline" sourceValue={settings.general.tagline}><input value={settings.general.tagline} onChange={(e) => update('general.tagline', e.target.value)} /></Field>
        <Field label="لوگوی اصلی" full>
          <div className="settings-media-field">
            <label className={`settings-upload-button ${uploading === 'general.logoUrl' ? 'is-loading' : ''}`}><FiUploadCloud /> {uploading === 'general.logoUrl' ? 'در حال آپلود...' : 'آپلود لوگو'}<input type="file" accept="image/*" onChange={(e) => uploadImage(e, 'general.logoUrl')} /></label>
            <input className="is-ltr" value={settings.general.logoUrl} onChange={(e) => update('general.logoUrl', e.target.value)} placeholder="https://... یا /uploads/..." />
            {settings.general.logoUrl && <img src={resolveSettingMediaUrl(settings.general.logoUrl)} alt="پیش‌نمایش لوگو" />}
          </div>
        </Field>
        <Field label="فاوآیکون" help="تصویر کوچک کنار عنوان مرورگر" full>
          <div className="settings-media-field is-compact">
            <label className={`settings-upload-button ${uploading === 'general.faviconUrl' ? 'is-loading' : ''}`}><FiImage /> انتخاب تصویر<input type="file" accept="image/*" onChange={(e) => uploadImage(e, 'general.faviconUrl')} /></label>
            <input className="is-ltr" value={settings.general.faviconUrl} onChange={(e) => update('general.faviconUrl', e.target.value)} />
            {settings.general.faviconUrl && <img src={resolveSettingMediaUrl(settings.general.faviconUrl)} alt="فاوآیکون" />}
          </div>
        </Field>
        <div className="settings-colors is-full">
          {[
            ['primaryDark', 'اصلی تیره'], ['primary', 'اصلی'], ['primaryLight', 'اصلی روشن'], ['secondary', 'ثانویه'], ['secondaryLight', 'ثانویه روشن'],
          ].map(([key, label]) => (
            <label key={key}><span>{label}</span><div><input type="color" value={settings.general[key]} onChange={(e) => update(`general.${key}`, e.target.value)} /><input className="is-ltr" value={settings.general[key]} onChange={(e) => update(`general.${key}`, e.target.value)} /></div></label>
          ))}
        </div>
        <Field label="توضیح فوتر" full translationPath="general.footerDescription" sourceValue={settings.general.footerDescription} multiline><textarea value={settings.general.footerDescription} onChange={(e) => update('general.footerDescription', e.target.value)} /></Field>
        <Field label="متن کپی‌رایت" translationPath="general.copyrightText" sourceValue={settings.general.copyrightText}><input value={settings.general.copyrightText} onChange={(e) => update('general.copyrightText', e.target.value)} /></Field>
        <Field label="لینک نشان اعتماد"><input className="is-ltr" value={settings.general.trustSealUrl} onChange={(e) => update('general.trustSealUrl', e.target.value)} /></Field>
        <Field label="تصویر نشان اعتماد" full><input className="is-ltr" value={settings.general.trustSealImageUrl} onChange={(e) => update('general.trustSealImageUrl', e.target.value)} /></Field>
      </div>
    </>
  );

  const renderContact = () => (
    <>
      <SectionHeading icon={FiPhone} title="اطلاعات تماس و شبکه‌های اجتماعی" description="این اطلاعات در صفحه ارتباط با ما و فوتر وب‌سایت استفاده می‌شوند." />
      <div className="settings-grid">
        <Field label="تلفن ثابت"><input value={settings.contact.phone} onChange={(e) => update('contact.phone', e.target.value)} /></Field>
        <Field label="شماره موبایل"><input value={settings.contact.mobile} onChange={(e) => update('contact.mobile', e.target.value)} /></Field>
        <Field label="ایمیل پشتیبانی"><input type="email" className="is-ltr" value={settings.contact.supportEmail} onChange={(e) => update('contact.supportEmail', e.target.value)} /></Field>
        <Field label="ساعات کاری" translationPath="contact.workingHours" sourceValue={settings.contact.workingHours}><input value={settings.contact.workingHours} onChange={(e) => update('contact.workingHours', e.target.value)} /></Field>
        <Field label="آدرس" full translationPath="contact.address" sourceValue={settings.contact.address} multiline><textarea value={settings.contact.address} onChange={(e) => update('contact.address', e.target.value)} /></Field>
        <Field label="آدرس Embed نقشه" help="فقط مقدار src کد نقشه را وارد کنید." full><input className="is-ltr" value={settings.contact.mapEmbedUrl} onChange={(e) => update('contact.mapEmbedUrl', e.target.value)} /></Field>
        <div className="settings-subsection is-full">
          <h3><FiLink /> شبکه‌های اجتماعی</h3>
          <div className="settings-grid">
            {['instagram', 'telegram', 'whatsapp', 'linkedin', 'x', 'youtube'].map((network) => (
              <Field key={network} label={network}><input className="is-ltr" value={settings.contact.social[network]} onChange={(e) => update(`contact.social.${network}`, e.target.value)} placeholder="https://..." /></Field>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const renderEmail = () => (
    <>
      <SectionHeading icon={FiMail} title="تنظیمات ایمیل" description="اتصال SMTP برای ارسال اعلان‌های خودکار و ایمیل‌های سیستمی." />
      <div className="settings-toggles"><Toggle checked={settings.email.enabled} onChange={(value) => update('email.enabled', value)} label="فعال‌سازی ارسال ایمیل" help="در حالت غیرفعال هیچ اعلان ایمیلی ارسال نمی‌شود." /></div>
      <div className="settings-grid">
        <Field label="SMTP Host"><input className="is-ltr" value={settings.email.host} onChange={(e) => update('email.host', e.target.value)} placeholder="mail.example.com" /></Field>
        <Field label="پورت SMTP"><input type="number" min="1" max="65535" value={settings.email.port} onChange={(e) => update('email.port', e.target.value)} /></Field>
        <Field label="امنیت اتصال"><select value={settings.email.secure ? 'ssl' : 'starttls'} onChange={(e) => update('email.secure', e.target.value === 'ssl')}><option value="ssl">SSL/TLS مستقیم</option><option value="starttls">STARTTLS</option></select></Field>
        <Field label="نام کاربری"><input className="is-ltr" value={settings.email.username} onChange={(e) => update('email.username', e.target.value)} /></Field>
        <Field label="رمز عبور" help={settings.email.passwordConfigured ? 'رمز قبلی محفوظ است؛ برای تغییر، مقدار جدید وارد کنید.' : 'هنوز رمزی ذخیره نشده است.'}><input type="password" autoComplete="new-password" className="is-ltr" value={settings.email.password} onChange={(e) => update('email.password', e.target.value)} placeholder={settings.email.passwordConfigured ? '•••••••• (بدون تغییر)' : ''} /></Field>
        <Field label="ایمیل فرستنده"><input type="email" className="is-ltr" value={settings.email.fromEmail} onChange={(e) => update('email.fromEmail', e.target.value)} /></Field>
        <Field label="نام فرستنده"><input value={settings.email.fromName} onChange={(e) => update('email.fromName', e.target.value)} /></Field>
        <Field label="ایمیل دریافت‌کننده آزمایشی"><input type="email" className="is-ltr" value={settings.email.testRecipient} onChange={(e) => update('email.testRecipient', e.target.value)} /></Field>
      </div>
      <div className="settings-email-template">
        <header>
          <span><FiCode /></span>
          <div><h3>قالب ظاهری ایمیل‌ها</h3><p>این قالب روی اعلان‌ها، بازیابی رمز عبور و تمام ایمیل‌های سیستمی اعمال می‌شود.</p></div>
        </header>
        <div className="settings-template-variables is-email-layout"><strong>متغیرهای قالب:</strong>{EMAIL_LAYOUT_VARIABLES.map((variable) => <code key={variable}>{variable}</code>)}</div>
        <Field label="کد HTML قالب ایمیل" help="متغیر {{content}} محل قرارگرفتن متن اصلی ایمیل است و نباید حذف شود." full>
          <textarea
            className="settings-code-editor"
            dir="ltr"
            spellCheck="false"
            value={settings.email.templateHtml}
            onChange={(e) => update('email.templateHtml', e.target.value)}
          />
        </Field>
      </div>
      <button type="button" className="settings-test-button" disabled={testing === 'email'} onClick={() => runTest('email', { recipient: settings.email.testRecipient })}><FiSend /> {testing === 'email' ? 'در حال بررسی و ارسال...' : 'بررسی اتصال و ارسال ایمیل آزمایشی'}</button>
    </>
  );

  const renderSms = () => (
    <>
      <SectionHeading icon={FiMessageSquare} title="تنظیمات پنل پیامکی" description="اتصال مستقیم به Panelchi برای ارسال اعلان‌ها و پیامک‌های سیستمی." />
      <div className="settings-toggles"><Toggle checked={settings.sms.enabled} onChange={(value) => update('sms.enabled', value)} label="فعال‌سازی ارسال پیامک" help="رویدادهایی که پیامک آن‌ها روشن باشد از این اتصال استفاده می‌کنند." /></div>
      <div className="settings-grid">
        <Field label="سرویس‌دهنده"><select value={settings.sms.provider} onChange={(e) => update('sms.provider', e.target.value)}><option value="panelchi">Panelchi</option><option value="generic">REST عمومی</option></select></Field>
        <Field
          label={settings.sms.provider === 'panelchi' ? 'توکن API پنلچی' : 'API Key'}
          help={settings.sms.provider === 'panelchi'
            ? (settings.sms.apiKeyConfigured ? 'توکن قبلی محفوظ است؛ فقط برای تغییر، مقدار جدید وارد کنید.' : 'توکن را از بخش وب‌سرویس پنلچی دریافت کنید.')
            : (settings.sms.apiKeyConfigured ? 'کلید قبلی محفوظ است؛ فقط برای تغییر، مقدار جدید وارد کنید.' : '')}
        >
          <input type="password" autoComplete="new-password" className="is-ltr" value={settings.sms.apiKey} onChange={(e) => update('sms.apiKey', e.target.value)} placeholder={settings.sms.apiKeyConfigured ? '•••••••• (بدون تغییر)' : ''} />
        </Field>
        {settings.sms.provider === 'generic' && (
          <>
            <Field label="نام کاربری (اختیاری)"><input className="is-ltr" value={settings.sms.username} onChange={(e) => update('sms.username', e.target.value)} placeholder={settings.sms.usernameConfigured ? 'مقدار قبلی محفوظ است' : ''} /></Field>
            <Field label="رمز عبور (اختیاری)"><input type="password" autoComplete="new-password" className="is-ltr" value={settings.sms.password} onChange={(e) => update('sms.password', e.target.value)} placeholder={settings.sms.passwordConfigured ? '•••••••• (بدون تغییر)' : ''} /></Field>
          </>
        )}
        <Field label={settings.sms.provider === 'panelchi' ? 'سرشماره ارسال (Source Number)' : 'شماره فرستنده'} help={settings.sms.provider === 'panelchi' ? 'یکی از سرشماره‌های فعال حساب پنلچی را وارد کنید.' : ''}><input className="is-ltr" value={settings.sms.senderNumber} onChange={(e) => update('sms.senderNumber', e.target.value)} placeholder={settings.sms.provider === 'panelchi' ? '10001' : ''} /></Field>
        <Field label="شماره دریافت‌کننده آزمایشی"><input className="is-ltr" value={settings.sms.testNumber} onChange={(e) => update('sms.testNumber', e.target.value)} placeholder="09123456789" /></Field>
        {settings.sms.provider === 'panelchi' && <Field label="اسلاگ پترن پیامک آزمایشی" help="پترن haryad-test باید با متن ثابت «هریاد: اتصال پنل پیامکی با موفقیت انجام شد.» در پنلچی تأیید شده باشد."><input className="is-ltr" value={settings.sms.testPattern} onChange={(e) => update('sms.testPattern', e.target.value.toLowerCase())} placeholder="haryad-test" /></Field>}
        {settings.sms.provider === 'generic' && <Field label="آدرس HTTPS سرویس REST" full><input className="is-ltr" value={settings.sms.endpoint} onChange={(e) => update('sms.endpoint', e.target.value)} placeholder="https://sms-provider.example/api/send" /></Field>}
      </div>
      {settings.sms.provider === 'panelchi' && <div className="settings-security-note"><FiShield /><span>ارسال Panelchi از مسیر رسمی /sms/pattern و با Bearer Token انجام می‌شود. توکن پس از ذخیره رمزگذاری شده و مقدار کامل آن دوباره به مرورگر برنمی‌گردد.</span></div>}
      <button type="button" className="settings-test-button" disabled={testing === 'sms'} onClick={() => runTest('sms', { phone: settings.sms.testNumber })}><FiSend /> {testing === 'sms' ? 'در حال ارسال...' : 'ارسال پیامک آزمایشی'}</button>
    </>
  );

  const renderPayment = () => (
    <>
      <SectionHeading icon={FiCreditCard} title="درگاه پرداخت سپهر" description="کنترل درگاه بانک صادرات و شرایط شروع پرداخت." />
      <div className="settings-toggles"><Toggle checked={settings.payment.enabled} onChange={(value) => update('payment.enabled', value)} label="فعال‌سازی درگاه سپهر" help="در حالت غیرفعال درخواست پرداخت جدید ساخته نمی‌شود؛ Callback تراکنش‌های قبلی همچنان بررسی می‌شود." /></div>
      <div className="settings-grid">
        <Field label="عنوان درگاه" translationPath="payment.title" sourceValue={settings.payment.title}><input value={settings.payment.title} onChange={(e) => update('payment.title', e.target.value)} /></Field>
        <Field label="حداقل مبلغ پرداخت (تومان)"><input type="number" min="100" value={settings.payment.minimumAmount} onChange={(e) => update('payment.minimumAmount', e.target.value)} /></Field>
        {settings.payment.gatewayMode !== 'relay' && <Field label="شناسه ترمینال" help={settings.payment.terminalIdConfigured ? 'شناسه قبلی یا مقدار سرور تنظیم است؛ برای تغییر، شناسه جدید وارد کنید.' : 'شناسه ترمینال هنوز تنظیم نشده است.'} full><input type="password" autoComplete="new-password" className="is-ltr" value={settings.payment.terminalId} onChange={(e) => update('payment.terminalId', e.target.value)} placeholder={settings.payment.terminalIdConfigured ? '•••••••• (بدون تغییر)' : ''} /></Field>}
      </div>
      <div className="settings-security-note"><FiShield /><span>{settings.payment.gatewayMode === 'relay' ? (settings.payment.relayConfigured ? 'حالت واسط ایران فعال است. GetToken، Callback و Advice روی pay.zhirai.com انجام می‌شوند و نتیجه با امضای HMAC به هریاد می‌رسد. شناسه ترمینال فقط روی سرور ایران نگهداری می‌شود.' : 'حالت واسط ایران انتخاب شده، اما آدرس یا کلید مشترک آن در تنظیمات سرور هریاد ناقص است.') : 'شناسه ترمینال پس از ذخیره رمزگذاری می‌شود و مقدار کامل آن دوباره به مرورگر برنمی‌گردد. ارتباط GetToken و Advice از Backend و روی HTTPS استاندارد سپهر انجام می‌شود.'}</span></div>
      <button type="button" className="settings-test-button" disabled={testing === 'payment'} onClick={() => runTest('payment')}><FiServer /> {testing === 'payment' ? 'در حال بررسی اتصال...' : settings.payment.gatewayMode === 'relay' ? 'بررسی اتصال به واسط ایران' : 'بررسی اتصال به سرور سپهر'}</button>
    </>
  );

  const renderStore = () => (
    <>
      <SectionHeading icon={FiShoppingBag} title="فروشگاه و محصولات دانلودی" description="قوانین فایل محصولات، دسترسی خریداران و شناسه سفارش‌ها." />
      <div className="settings-grid">
        <Field label="تعداد دفعات مجاز دانلود" help="عدد صفر یعنی بدون محدودیت."><input type="number" min="0" max="10000" value={settings.store.downloadLimit} onChange={(e) => update('store.downloadLimit', e.target.value)} /></Field>
        <Field label="اعتبار دانلود (روز)" help="عدد صفر یعنی بدون انقضا."><input type="number" min="0" max="3650" value={settings.store.downloadValidityDays} onChange={(e) => update('store.downloadValidityDays', e.target.value)} /></Field>
        <Field label="حداکثر حجم فایل محصول (MB)" help="از ۱ تا ۱۰۲۴ مگابایت"><input type="number" min="1" max="1024" value={settings.store.maxProductFileSizeMb} onChange={(e) => update('store.maxProductFileSizeMb', e.target.value)} /></Field>
        <Field label="پیشوند شماره سفارش"><input className="is-ltr" value={settings.store.orderPrefix} onChange={(e) => update('store.orderPrefix', e.target.value)} placeholder="HRD-" /></Field>
      </div>
      <div className="settings-info-card"><FiCheckCircle /><div><strong>سازگار با سفارش‌های قبلی</strong><span>سفارش‌های قدیمی بدون شماره سفارشی همچنان با شناسه قبلی نمایش داده می‌شوند و محدودیت‌ها از زمان پرداخت ثبت‌شده یا آخرین به‌روزرسانی محاسبه خواهند شد.</span></div></div>
    </>
  );

  const renderNotifications = () => {
    const selectedMeta = (
      filteredNotificationEvents.find((event) => event.id === selectedNotificationEvent)
      || filteredNotificationEvents[0]
      || null
    );
    const selectedEvent = selectedMeta
      ? settings.notifications.events[selectedMeta.id]
      : null;
    const selectedCategory = selectedMeta
      ? NOTIFICATION_CATEGORIES.find((category) => category.id === selectedMeta.category)
      : null;
    const SelectedCategoryIcon = selectedMeta
      ? NOTIFICATION_CATEGORY_ICONS[selectedMeta.category]
      : FiBell;
    const isAdminAudience = notificationAudience === 'admin';
    const emailEnabledField = isAdminAudience ? 'adminEmailEnabled' : 'emailEnabled';
    const smsEnabledField = isAdminAudience ? 'adminSmsEnabled' : 'smsEnabled';
    const emailSubjectField = isAdminAudience ? 'adminEmailSubject' : 'emailSubject';
    const emailBodyField = isAdminAudience ? 'adminEmailBody' : 'emailBody';
    const smsPatternField = isAdminAudience ? 'adminSmsPattern' : 'smsPattern';
    const smsBodyField = isAdminAudience ? 'adminSmsBody' : 'smsBody';
    const adminSummary = adminRecipientSummary
      || settings.notifications.adminRecipientSummary
      || {};
    const summaryCards = [
      {
        label: 'کل رویدادها',
        value: notificationSummary.totalEvents,
        note: `در ${(NOTIFICATION_CATEGORIES.length - 1).toLocaleString('fa-IR')} گروه`,
        icon: FiBell,
        tone: 'blue',
      },
      {
        label: 'رویداد فعال',
        value: notificationSummary.activeEvents,
        note: 'حداقل یک کانال روشن',
        icon: FiCheckCircle,
        tone: 'green',
      },
      {
        label: 'دارای ایمیل',
        value: notificationSummary.emailEvents,
        note: 'کاربر یا مدیر',
        icon: FiMail,
        tone: 'violet',
      },
      {
        label: 'دارای پیامک',
        value: notificationSummary.smsEvents,
        note: 'پترن Panelchi',
        icon: FiMessageSquare,
        tone: 'orange',
      },
    ];

    return (
      <>
        <SectionHeading icon={FiBell} title="مرکز اعلان‌ها" description="وضعیت همه رویدادها را یک‌جا ببینید و فقط پیام موردنیاز را برای ویرایش باز کنید." />

        <div className="notification-control-room">
          <div className="notification-summary-grid">
            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
              <article key={label} className={`notification-summary-card is-${tone}`}>
                <span><Icon /></span>
                <div>
                  <strong>{Number(value || 0).toLocaleString('fa-IR')}</strong>
                  <p>{label}</p>
                  <small>{note}</small>
                </div>
              </article>
            ))}
          </div>

          <section className="notification-admin-card">
            <div className="notification-admin-intro">
              <span><FiUsers /></span>
              <div>
                <h3>ارسال به مدیران</h3>
                <p>گیرنده‌ها خودکار از کاربران دارای نقش «مدیر» خوانده می‌شوند.</p>
              </div>
            </div>
            <div className="notification-admin-channels">
              <Toggle checked={settings.notifications.admin.emailEnabled} onChange={(value) => update('notifications.admin.emailEnabled', value)} label="ایمیل مدیران" />
              <Toggle checked={settings.notifications.admin.smsEnabled} onChange={(value) => update('notifications.admin.smsEnabled', value)} label="پیامک مدیران" />
            </div>
            <div className="notification-admin-stats">
              <span><strong>{Number(adminSummary.total || 0).toLocaleString('fa-IR')}</strong> مدیر</span>
              <span><FiMail /><strong>{Number(adminSummary.withEmail || 0).toLocaleString('fa-IR')}</strong> ایمیل</span>
              <span><FiMessageSquare /><strong>{Number(adminSummary.withPhone || 0).toLocaleString('fa-IR')}</strong> موبایل</span>
            </div>
          </section>

          <section className={`notification-guide ${notificationGuideOpen ? 'is-open' : ''}`}>
            <button type="button" onClick={() => setNotificationGuideOpen((current) => !current)} aria-expanded={notificationGuideOpen}>
              <span><FiCode /></span>
              <div>
                <strong>راهنمای متغیرهای قالب</strong>
                <small>متغیرهای مجاز ایمیل و پترن Panelchi</small>
              </div>
              {notificationGuideOpen ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {notificationGuideOpen && (
              <div className="notification-guide-content">
                <div className="settings-template-variables">
                  <strong>متغیرهای ایمیل:</strong>
                  {TEMPLATE_VARIABLES.map((variable) => <code key={variable}>{variable}</code>)}
                </div>
                <div className="settings-template-variables is-sms-pattern">
                  <strong>متغیرهای پترن Panelchi:</strong>
                  {SMS_TEMPLATE_VARIABLES.map((variable) => <code key={variable}>{variable}</code>)}
                </div>
              </div>
            )}
          </section>

          <div className="notification-workspace">
            <aside className="notification-browser">
              <header>
                <div>
                  <strong>رویدادها</strong>
                  <small>{filteredNotificationEvents.length.toLocaleString('fa-IR')} مورد نمایش داده می‌شود</small>
                </div>
                <FiFilter />
              </header>

              <label className="notification-search">
                <FiSearch />
                <input
                  value={notificationQuery}
                  onChange={(event) => setNotificationQuery(event.target.value)}
                  placeholder="جست‌وجوی رویداد..."
                />
              </label>

              <div className="notification-category-tabs" role="tablist" aria-label="دسته‌بندی رویدادها">
                {NOTIFICATION_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={notificationCategory === category.id ? 'is-active' : ''}
                    onClick={() => setNotificationCategory(category.id)}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className={`notification-active-filter ${notificationOnlyActive ? 'is-active' : ''}`}
                onClick={() => setNotificationOnlyActive((current) => !current)}
              >
                <span><i /></span>
                فقط رویدادهای فعال
              </button>

              <div className="notification-event-list">
                {filteredNotificationEvents.map((eventMeta) => {
                  const event = settings.notifications.events[eventMeta.id] || {};
                  const CategoryIcon = NOTIFICATION_CATEGORY_ICONS[eventMeta.category];
                  const activeChannels = [
                    event.emailEnabled,
                    event.smsEnabled,
                    event.adminEmailEnabled,
                    event.adminSmsEnabled,
                  ].filter(Boolean).length;
                  return (
                    <button
                      key={eventMeta.id}
                      type="button"
                      className={selectedMeta?.id === eventMeta.id ? 'is-selected' : ''}
                      onClick={() => setSelectedNotificationEvent(eventMeta.id)}
                    >
                      <span className="notification-event-icon"><CategoryIcon /></span>
                      <span className="notification-event-copy">
                        <strong>{eventMeta.label}</strong>
                        <small>{activeChannels ? `${activeChannels.toLocaleString('fa-IR')} کانال فعال` : 'همه کانال‌ها خاموش'}</small>
                      </span>
                      <span className={`notification-event-state ${activeChannels ? 'is-on' : ''}`} />
                      <FiChevronLeft />
                    </button>
                  );
                })}
                {!filteredNotificationEvents.length && (
                  <div className="notification-no-results">
                    <FiSearch />
                    <strong>رویدادی پیدا نشد</strong>
                    <small>عبارت جست‌وجو یا فیلتر را تغییر دهید.</small>
                  </div>
                )}
              </div>
            </aside>

            <section className="notification-editor">
              {selectedMeta && selectedEvent ? (
                <>
                  <header className="notification-editor-heading">
                    <div className="notification-editor-title">
                      <span><SelectedCategoryIcon /></span>
                      <div>
                        <small>{selectedCategory?.label}</small>
                        <h3>{selectedMeta.label}</h3>
                        <p>{selectedMeta.description}</p>
                      </div>
                    </div>
                    <span className="notification-edit-badge"><FiEdit3 /> در حال ویرایش</span>
                  </header>

                  <div className="notification-audience-tabs" role="tablist" aria-label="مخاطب اعلان">
                    <button
                      type="button"
                      className={!isAdminAudience ? 'is-active' : ''}
                      onClick={() => setNotificationAudience('user')}
                    >
                      <FiUser />
                      <span><strong>کاربر</strong><small>گیرندهٔ رویداد</small></span>
                    </button>
                    <button
                      type="button"
                      className={isAdminAudience ? 'is-active' : ''}
                      onClick={() => setNotificationAudience('admin')}
                    >
                      <FiUsers />
                      <span><strong>مدیران</strong><small>تمام مدیران واجد شرایط</small></span>
                    </button>
                  </div>

                  <div className="notification-channel-panel">
                    <div>
                      <strong>کانال‌های ارسال</strong>
                      <small>فقط کانال‌های روشن در این رویداد اجرا می‌شوند.</small>
                    </div>
                    <div className="notification-channel-toggles">
                      <Toggle
                        checked={selectedEvent[emailEnabledField]}
                        onChange={(value) => update(`notifications.events.${selectedMeta.id}.${emailEnabledField}`, value)}
                        label="ایمیل"
                      />
                      <Toggle
                        checked={selectedEvent[smsEnabledField]}
                        onChange={(value) => update(`notifications.events.${selectedMeta.id}.${smsEnabledField}`, value)}
                        label="پیامک"
                      />
                    </div>
                  </div>

                  {!selectedEvent[emailEnabledField] && !selectedEvent[smsEnabledField] && (
                    <div className="notification-editor-empty">
                      <span><FiBell /></span>
                      <strong>برای این مخاطب کانالی فعال نیست</strong>
                      <p>ایمیل یا پیامک را روشن کنید تا تنظیمات قالب همان کانال نمایش داده شود.</p>
                    </div>
                  )}

                  {selectedEvent[emailEnabledField] && (
                    <section className="notification-template-section is-email">
                      <header>
                        <span><FiMail /></span>
                        <div><strong>قالب ایمیل</strong><small>موضوع و متن ایمیل این رویداد</small></div>
                      </header>
                      <div className="settings-grid">
                        <Field label={`موضوع ایمیل ${isAdminAudience ? 'مدیر' : 'کاربر'}`} full>
                          <input
                            value={selectedEvent[emailSubjectField]}
                            onChange={(event) => update(`notifications.events.${selectedMeta.id}.${emailSubjectField}`, event.target.value)}
                          />
                        </Field>
                        <Field label={`متن ایمیل ${isAdminAudience ? 'مدیر' : 'کاربر'}`} full>
                          <textarea
                            value={selectedEvent[emailBodyField]}
                            onChange={(event) => update(`notifications.events.${selectedMeta.id}.${emailBodyField}`, event.target.value)}
                          />
                        </Field>
                      </div>
                    </section>
                  )}

                  {selectedEvent[smsEnabledField] && (
                    <section className="notification-template-section is-sms">
                      <header>
                        <span><FiMessageSquare /></span>
                        <div><strong>پترن پیامک</strong><small>اسلاگ و متن دقیق تأییدشده در Panelchi</small></div>
                      </header>
                      <div className="settings-grid">
                        <Field
                          label={`اسلاگ پترن ${isAdminAudience ? 'مدیر' : 'کاربر'}`}
                          help={isAdminAudience ? 'برای هر مدیر یک درخواست پترنی مستقل ارسال می‌شود.' : 'باید دقیقاً با آیدی پترن تأییدشده در Panelchi یکسان باشد.'}
                          full
                        >
                          <input
                            key={`${selectedMeta.id}-${notificationAudience}-${smsPatternField}`}
                            id={`notification-${selectedMeta.id}-${notificationAudience}-sms-pattern`}
                            name={`notification-${selectedMeta.id}-${notificationAudience}-sms-pattern`}
                            className="is-ltr"
                            autoComplete="off"
                            value={selectedEvent[smsPatternField] || ''}
                            onChange={(event) => update(`notifications.events.${selectedMeta.id}.${smsPatternField}`, event.target.value.toLowerCase())}
                          />
                        </Field>
                        <Field
                          label={`متن دقیق پترن ${isAdminAudience ? 'مدیر' : 'کاربر'}`}
                          help="این متن را بدون تغییر در Panelchi ثبت کنید؛ نام هریاد ثابت و مبدأ ارسال مشخص است."
                          full
                        >
                          <textarea
                            key={`${selectedMeta.id}-${notificationAudience}-${smsBodyField}`}
                            id={`notification-${selectedMeta.id}-${notificationAudience}-sms-body`}
                            name={`notification-${selectedMeta.id}-${notificationAudience}-sms-body`}
                            autoComplete="off"
                            value={selectedEvent[smsBodyField] || ''}
                            onChange={(event) => update(`notifications.events.${selectedMeta.id}.${smsBodyField}`, event.target.value)}
                          />
                        </Field>
                      </div>
                    </section>
                  )}
                </>
              ) : (
                <div className="notification-editor-empty is-filtered">
                  <span><FiFilter /></span>
                  <strong>رویدادی برای ویرایش وجود ندارد</strong>
                  <p>فیلتر یا عبارت جست‌وجو را تغییر دهید.</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </>
    );
  };

  const renderSeo = () => (
    <>
      <SectionHeading icon={FiSearch} title="سئو، اشتراک‌گذاری و آمار" description="مقادیر پیش‌فرض متا و شناسه ابزارهای رسمی تحلیل بازدید." />
      <div className="settings-grid">
        <Field label="عنوان پیش‌فرض" full translationPath="seo.defaultTitle" sourceValue={settings.seo.defaultTitle}><input value={settings.seo.defaultTitle} onChange={(e) => update('seo.defaultTitle', e.target.value)} /></Field>
        <Field label="توضیحات پیش‌فرض" full translationPath="seo.defaultDescription" sourceValue={settings.seo.defaultDescription} multiline><textarea value={settings.seo.defaultDescription} onChange={(e) => update('seo.defaultDescription', e.target.value)} /></Field>
        <Field label="تصویر پیش‌فرض اشتراک‌گذاری" full><input className="is-ltr" value={settings.seo.defaultShareImage} onChange={(e) => update('seo.defaultShareImage', e.target.value)} placeholder="https://... یا /uploads/..." /></Field>
        <Field label="Google Analytics Measurement ID" help="با قالب G-XXXXXXXXXX"><input className="is-ltr" value={settings.seo.googleAnalyticsId} onChange={(e) => update('seo.googleAnalyticsId', e.target.value.toUpperCase())} placeholder="G-XXXXXXXXXX" /></Field>
        <Field label="Google Tag Manager ID" help="با قالب GTM-XXXXXXX"><input className="is-ltr" value={settings.seo.googleTagManagerId} onChange={(e) => update('seo.googleTagManagerId', e.target.value.toUpperCase())} placeholder="GTM-XXXXXXX" /></Field>
      </div>
      <div className="settings-security-note"><FiShield /><span>به‌جای اجرای کد دلخواه، فقط شناسه‌های معتبر Analytics و Tag Manager پذیرفته می‌شوند تا امنیت صفحات عمومی حفظ شود.</span></div>
    </>
  );

  const renderStatus = () => (
    <>
      <SectionHeading icon={FiTool} title="وضعیت و دسترسی سایت" description="نمایش صفحه تعمیرات و کنترل ثبت‌نام حساب‌های جدید." />
      <div className="settings-toggles is-stack">
        <Toggle checked={settings.status.maintenanceEnabled} onChange={(value) => update('status.maintenanceEnabled', value)} label="حالت تعمیر و نگهداری" help="بازدیدکنندگان صفحه تعمیرات را می‌بینند؛ مدیر واردشده همچنان به سایت و پنل دسترسی دارد." />
        <Toggle checked={settings.status.registrationEnabled} onChange={(value) => update('status.registrationEnabled', value)} label="اجازه ثبت‌نام کاربران جدید" help="این محدودیت هم در رابط سایت و هم در API اعمال می‌شود." />
      </div>
      <div className="settings-grid">
        <Field label="عنوان صفحه تعمیرات" full translationPath="status.maintenanceTitle" sourceValue={settings.status.maintenanceTitle}><input value={settings.status.maintenanceTitle} onChange={(e) => update('status.maintenanceTitle', e.target.value)} /></Field>
        <Field label="متن صفحه تعمیرات" full translationPath="status.maintenanceMessage" sourceValue={settings.status.maintenanceMessage} multiline><textarea value={settings.status.maintenanceMessage} onChange={(e) => update('status.maintenanceMessage', e.target.value)} /></Field>
      </div>
      {settings.status.maintenanceEnabled && <div className="settings-warning"><FiTool /><div><strong>حالت تعمیرات روشن خواهد بود</strong><span>پس از ذخیره، کاربران عادی به محتوای سایت دسترسی نخواهند داشت. صفحه ورود مدیر باز می‌ماند.</span></div></div>}
    </>
  );

  const renderers = { general: renderGeneral, contact: renderContact, email: renderEmail, sms: renderSms, payment: renderPayment, store: renderStore, notifications: renderNotifications, seo: renderSeo, status: renderStatus };
  const SectionIcon = currentSection.icon;

  return (
    <div className="admin-settings-panel">
      <div className="settings-overview">
        <div><span><FiSettings /></span><div><h2>تنظیمات وب‌سایت</h2><p>همه سرویس‌ها و اطلاعات سراسری هریاد از این مرکز کنترل می‌شوند.</p></div></div>
        <span className="settings-database-badge"><FiCheckCircle /> ذخیره سراسری در دیتابیس</span>
      </div>

      {notice && <div className={`settings-notice is-${notice.type}`} role="status">{notice.type === 'success' ? <FiCheckCircle /> : <FiShield />}<span>{notice.text}</span><button type="button" className="ui-close-button" onClick={() => setNotice(null)} aria-label="بستن پیام">×</button></div>}

      <div className="settings-layout">
        <nav className="settings-nav" aria-label="دسته‌های تنظیمات">
          {SETTINGS_SECTIONS.map(({ id, label, description, icon: Icon }) => (
            <button key={id} type="button" className={activeSection === id ? 'active' : ''} onClick={() => { setActiveSection(id); setNotice(null); }}>
              <span><Icon /></span><div><strong>{label}</strong><small>{description}</small></div>
            </button>
          ))}
        </nav>

        <section className="settings-content">
          <div className="settings-mobile-title"><SectionIcon /><span>{currentSection.label}</span></div>
          {renderers[activeSection]()}
          <footer className="settings-save-bar">
            <div><FiShield /><span>اطلاعات حساس رمزگذاری شده و هرگز به‌صورت کامل نمایش داده نمی‌شوند.</span></div>
            <button type="button" disabled={saving === activeSection} onClick={() => saveSection(activeSection)}>
              {saving === activeSection ? <FiLoader className="is-spinning" /> : <FiSave />}
              {saving === activeSection ? 'در حال ذخیره...' : `ذخیره ${currentSection.label}`}
            </button>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default AdminSettingsPanel;
